import { computed, onScopeDispose, watch } from 'vue'
import { useProjectStore } from '@/stores/project'
import { useAppStore } from '@/stores/app'
import { useNotificationsStore } from '@/stores/notifications'
import { useRouter } from 'vue-router'

const POLL_INTERVAL_MS = 60000
const ID_PREFIX = 'project-invite-'

// invite_v3 timestamps are naive UTC; without a zone the browser would read them as local time.
const toIsoUtc = (value) => (value && !/(Z|[+-]\d{2}:?\d{2})$/.test(value) ? `${value}Z` : value)

export function useProjectInviteNotifications() {
  const projectStore = useProjectStore()
  const appStore = useAppStore()
  const notificationsStore = useNotificationsStore()
  const router = useRouter()

  const isLoggedIn = computed(() => appStore.isLoggedIn)
  const invites = computed(() => projectStore.allInvites || [])

  let timer = null

  const poll = () => {
    if (document.visibilityState !== 'visible') return
    projectStore.fetchAllInvites({ silent: true })
  }

  const startPolling = () => {
    if (!timer) timer = setInterval(poll, POLL_INTERVAL_MS)
  }

  const stopPolling = () => {
    clearInterval(timer)
    timer = null
  }

  const sync = () => {
    const ids = new Set(invites.value.map(invite => `${ID_PREFIX}${invite.uuid}`))

    notificationsStore.notifications
      .filter(n => n.id.startsWith(ID_PREFIX) && !ids.has(n.id))
      .forEach(n => notificationsStore.removeNotification(n.id))

    invites.value.forEach((invite) => {
      const id = `${ID_PREFIX}${invite.uuid}`
      if (notificationsStore.getNotificationById(id)) return
      notificationsStore.addNotification({
        id,
        type: 'project_invite',
        title: 'Project invitation',
        description: `${invite.inviter_name} invited you to ${invite.project_name}`,
        createdAt: toIsoUtc(invite.created_at),
        sticky: true,
        action: {
          label: 'Open',
          handler: () => router.push({ name: 'Invite', params: { inviteCode: invite.uuid } })
        }
      })
    })
  }

  watch([isLoggedIn, invites], () => {
    if (!isLoggedIn.value) {
      stopPolling()
      return
    }
    sync()
    startPolling()
  }, { immediate: true })

  onScopeDispose(stopPolling)
}
